/**
 * ===================================================================================================
 * AuthScreen.js - 인증 화면 메인 컴포넌트
 * ===================================================================================================
 *
 * 📍 위치: 프론트엔드 > client/src/components/auth/AuthScreen.js
 *
 * 🎯 주요 기능:
 *    - 로그인 / 회원가입 화면 전환
 *    - 로그인 성공 시 콜백 호출
 *    - 회원가입 성공 알림 표시
 *    - CustomAlertModal을 통한 메시지 표시
 *
 * 🔗 연결된 파일:
 *    - ./LoginForm.js - 로그인 폼 컴포넌트
 *    - ./RegisterForm.js - 회원가입 폼 컴포넌트
 *    - ../modals/CustomAlertModal.js - 커스텀 알림 모달
 *
 * 💡 UI 위치:
 *    - 화면: 앱 시작 시 인증 화면
 *    - 접근: 비로그인 상태에서 자동 표시
 *    - 섹션: 로그인 폼 ↔ 회원가입 폼 전환
 *
 * ✏️ 수정 가이드:
 *    - 이 파일을 수정하면: 인증 화면 전체 레이아웃 및 전환 로직 변경
 *    - 폼 전환 로직 변경: showLogin 상태 및 버튼 핸들러 수정
 *    - 알림 메시지 변경: showAlert 호출 시 전달하는 메시지 수정
 *
 * 📝 참고사항:
 *    - 로그인과 회원가입 폼 간 전환은 showLogin 상태로 제어
 *    - 회원가입 성공 시 자동으로 로그인 화면으로 전환
 *    - 회원가입 취소 시 확인 메시지 표시
 *
 * ===================================================================================================
 */

import React, { useState, useCallback, useEffect } from 'react';
import CustomAlertModal from '../modals/CustomAlertModal';
import LoginForm from './LoginForm';
import RegisterForm from './RegisterForm';
import { API_BASE_URL } from '../../config/apiConfig';
import { detectInAppBrowser, isIOSDevice } from '../../utils/authBrowser';

/**
 * AuthScreen - 인증 화면 메인 컴포넌트
 *
 * @param {Object} props - 컴포넌트 props
 * @param {Function} props.onLoginSuccess - 로그인 성공 시 호출되는 콜백 함수
 *
 * @returns {JSX.Element} 인증 화면 UI
 */
const AuthScreen = ({ onLoginSuccess }) => {
  // ?mode=login → 로그인 탭, ?mode=register → 회원가입 탭
  // mode 없으면 pendingInviteCode 있을 때 회원가입 탭을 기본으로 표시
  const params = new URLSearchParams(window.location.search);
  const urlMode = params.get('mode');
  const hasInviteFromUrl = !!params.get('invite');
  const [hasPendingInvite, setHasPendingInvite] = useState(() => (
    hasInviteFromUrl || !!localStorage.getItem('pendingInviteCode')
  ));
  const hasVerifyToken = !!params.get('verifyToken');
  const [showLogin, setShowLogin] = useState(
    hasVerifyToken ? false
    : urlMode === 'register' ? false
    : urlMode === 'login' ? true
    : !hasPendingInvite
  );

  // 이메일 링크 클릭 인증 상태
  const [preVerifiedEmail, setPreVerifiedEmail] = useState(null);
  const [isVerifyTab, setIsVerifyTab] = useState(false); // 이 탭이 인증용 새 탭인지

  // ?verifyToken=xxx&email=xxx 처리: 서버에 토큰 검증 요청
  useEffect(() => {
    // invite 코드가 URL에 있으면 localStorage에 저장 (브라우저 전환 시 코드 보존)
    const inviteFromUrl = params.get('invite');
    if (inviteFromUrl) {
      localStorage.setItem('pendingInviteCode', inviteFromUrl);
      setHasPendingInvite(true);
    }

    const vToken = params.get('verifyToken');
    const vEmail = params.get('email');
    if (!vToken || !vEmail) return;
    setIsVerifyTab(true);
    // URL 파라미터 즉시 제거 (재처리 방지)
    window.history.replaceState({}, '', '/auth');
    fetch(`${API_BASE_URL}/api/auth/verify-email?token=${encodeURIComponent(vToken)}&email=${encodeURIComponent(vEmail)}`)
      .then(r => r.json())
      .then(data => {
        if (data.email) {
          // 원래 탭에 인증 완료 브로드캐스트 (storage 이벤트)
          localStorage.setItem('emailVerified', JSON.stringify({ email: data.email, ts: Date.now() }));
          // 인증 완료 즉시 회원가입 폼으로 이동
          setPreVerifiedEmail(data.email);
          setIsVerifyTab(false);
          setShowLogin(false);
        } else {
          setAlertModal({ isOpen: true, title: '인증 실패', message: data.msg || '인증 링크가 만료되었거나 유효하지 않습니다.', type: 'error', showCancel: false, onConfirm: null });
        }
      })
      .catch(() => {
        setAlertModal({ isOpen: true, title: '오류', message: '네트워크 오류가 발생했습니다.', type: 'error', showCancel: false, onConfirm: null });
      });
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // CustomAlert 상태
  const [alertModal, setAlertModal] = useState({
    isOpen: false,
    title: '',
    message: '',
    type: 'info',
    showCancel: false,
    onConfirm: null
  });

  /**
   * showAlert - 알림 모달 표시 함수
   *
   * @description CustomAlertModal을 통해 알림 메시지를 표시
   * @param {string} message - 표시할 메시지
   * @param {string} type - 알림 타입 (info, success, warning, error)
   * @param {string} title - 알림 제목
   * @param {boolean} showCancel - 취소 버튼 표시 여부
   * @param {Function} onConfirm - 확인 버튼 클릭 시 콜백 함수
   */
  const showAlert = useCallback((message, type = 'info', title = '', showCancel = false, onConfirm = null) => {
    setAlertModal({
      isOpen: true,
      title,
      message,
      type,
      showCancel,
      onConfirm
    });
  }, []);

  /**
   * closeAlert - 알림 모달 닫기 함수
   *
   * @description 열려있는 알림 모달을 닫음
   */
  const closeAlert = useCallback(() => {
    setAlertModal(prev => ({ ...prev, isOpen: false }));
  }, []);

  const inAppBrowser = detectInAppBrowser();
  const iosDevice = isIOSDevice();
  const [copied, setCopied] = useState(false);

  const fallbackCopyLink = () => {
    const el = document.createElement('textarea');
    el.value = window.location.href;
    document.body.appendChild(el);
    el.select();
    document.execCommand('copy');
    document.body.removeChild(el);
    setCopied(true);
    setTimeout(() => setCopied(false), 3000);
  };

  const handleCopyLink = () => {
    const url = window.location.href;
    if (navigator.clipboard && window.isSecureContext) {
      navigator.clipboard.writeText(url).then(() => {
        setCopied(true);
        setTimeout(() => setCopied(false), 3000);
      }).catch(fallbackCopyLink);
      return;
    }

    fallbackCopyLink();
  };

  const inAppBannerHeight = inAppBrowser ? 120 : 0;
  const inviteBannerHeight = hasPendingInvite ? 48 : 0;
  const topPadding = inAppBannerHeight + inviteBannerHeight;

  return (
    <div className="flex items-center justify-center min-h-screen bg-gray-100" style={topPadding ? { paddingTop: `${topPadding}px` } : {}}>
      {inAppBrowser && (
        <div style={{
          position: 'fixed', top: 0, left: 0, right: 0, zIndex: 10000,
          background: '#f59e0b', color: '#1c1917',
          padding: '12px 16px', fontSize: '13px', fontWeight: 600,
          boxShadow: '0 2px 8px rgba(0,0,0,0.2)', textAlign: 'center',
          lineHeight: '1.6',
        }}>
          📱 앱 내 브라우저로 열렸습니다.
          {iosDevice ? (
            <>
              <br/>
              <span style={{ fontWeight: 400, fontSize: '12px' }}>
                홈화면 앱을 열려면 링크를 복사 후 <strong>Safari</strong>에서 붙여넣으세요.
              </span>
              <div style={{ marginTop: '8px', display: 'flex', gap: '8px', justifyContent: 'center' }}>
                <button
                  onClick={handleCopyLink}
                  style={{
                    background: copied ? '#16a34a' : '#1c1917', color: '#fff', border: 'none',
                    borderRadius: '6px', padding: '6px 16px', fontSize: '12px',
                    fontWeight: 700, cursor: 'pointer', transition: 'background 0.2s',
                  }}
                >
                  {copied ? '✓ 복사됨!' : '링크 복사'}
                </button>
              </div>
            </>
          ) : (
            <>
              <br/>
              <span style={{ fontWeight: 400, fontSize: '12px' }}>
                홈화면 앱을 열려면 <strong>Chrome</strong>으로 열어주세요.
              </span>
              <div style={{ marginTop: '8px' }}>
                <button
                  onClick={() => window.open(window.location.href, '_blank')}
                  style={{
                    background: '#1c1917', color: '#fff', border: 'none',
                    borderRadius: '6px', padding: '6px 16px', fontSize: '12px',
                    fontWeight: 700, cursor: 'pointer',
                  }}
                >
                  Chrome으로 열기
                </button>
              </div>
            </>
          )}
        </div>
      )}
      {hasPendingInvite && (
        <div style={{
          position: 'fixed', top: inAppBannerHeight, left: 0, right: 0, zIndex: 9999,
          background: 'linear-gradient(135deg, #2563eb, #7c3aed)',
          color: '#fff', textAlign: 'center', padding: '12px 16px',
          fontSize: '14px', fontWeight: 600, boxShadow: '0 2px 8px rgba(0,0,0,0.2)',
        }}>
          🎉 방 초대를 받으셨습니다! 회원가입 후 자동으로 방에 참여됩니다.
        </div>
      )}
      {isVerifyTab ? (
        <div className="bg-white rounded-xl shadow-lg p-8 text-center max-w-sm w-full mx-4">
          <div className="text-5xl mb-4">⏳</div>
          <h2 className="text-xl font-bold text-gray-800 mb-2">인증 중...</h2>
          <p className="text-gray-500 text-sm">잠시만 기다려주세요.</p>
        </div>
      ) : showLogin ? (
        <LoginForm
          onClose={() => {}}
          onRegisterClick={() => setShowLogin(false)}
          onLoginSuccess={onLoginSuccess}
        />
      ) : (
        <RegisterForm
          onClose={() => {
            showAlert('회원가입을 하지 않으시겠습니까?', 'warning', '회원가입 취소', true, () => {
              setShowLogin(true);
            });
          }}
          onRegisterSuccess={() => {
            showAlert('회원가입 성공! 로그인 해주세요.', 'success', '회원가입 성공', false, () => setShowLogin(true));
          }}
          onLoginClick={() => setShowLogin(true)}
          onLoginSuccess={onLoginSuccess}
          preVerifiedEmail={preVerifiedEmail}
        />
      )}
      
      <CustomAlertModal
        isOpen={alertModal.isOpen}
        onClose={closeAlert}
        onConfirm={alertModal.onConfirm}
        title={alertModal.title}
        message={alertModal.message}
        type={alertModal.type}
        showCancel={alertModal.showCancel}
      />
    </div>
  );
};

export default AuthScreen;